import { apiService } from './ApiService';
import { connectionService } from './ConnectionService';
import Order from '../models/Order';
import OrderItem from '../models/OrderItem';
import Money from '../utilities/Money';
import HashGen from '../utilities/HashGen';
import Store from '../wrappers/Store';
import { I18n } from '../wrappers/I18n';
import { OrderFlagType, OrderStatusType, PaymentTypeLabel } from '../types';
const ZERO = () => Money.parse(0);
const min = (a, b) => a.toUnit() <= b.toUnit() ? a : b;
const positive = (a) => a.toUnit() > 0 ? a : ZERO();
/**
 * Advances used in parts (`order.advanceInParts`: Montenegro, Bosnia).
 *
 * A final pays part of its total from an advance. Right after it, the closing takes what the final paid
 * off the advance's own lines (Montenegrin functional specification v5 §5.8) and names the final, which
 * the backend sends to EFI as IICRefs. Cancelling a final undoes its closing, so what it used is
 * available again (Route 1).
 */
const AdvanceFlowService = {
    /**
     * What is left of `advance` for `final`: its total less its live closings, and never more than its
     * other live finals left of it (a final from v8.1.10 used part of its advance without a closing).
     * `advance` must be the stored record — a final's own copy of it carries none of its closings.
     */
    left(advance, final) {
        const paidByFinals = advance.ordersFromAdvance()
            .filter(order => order.hash !== final.hash)
            .map(order => Order.find(order.hash) ?? order)
            .filter(order => !order.canceled())
            .reduce((acc, order) => acc.add(order.getPaidAdvance()), ZERO());
        return min(positive(advance.getRemainingAdvanceAmount()), positive(advance.getToPay().subtract(paidByFinals)));
    },
    /**
     * Whether the closing for a final that paid `paid` from `advance` reverses the advance in full (a
     * storno, which pays back every method the advance was paid by): the advance is untouched and the
     * final uses all of it. Any other closing takes the amount off its lines and pays back one method.
     */
    closesInFull(advance, paid) {
        return advance.correctedByOrders().length === 0 && paid.toUnit() >= advance.getRemainingAdvanceAmount().toUnit();
    },
    /** Issue the closing for `final`, a paid final from an advance. */
    async close(final) {
        if (!final.refersToAdvance() || !final.isPaid())
            return;
        const paid = final.getPaidAdvance();
        if (paid.toUnit() <= 0)
            return;
        // The closing names its final, and the server refuses a document that names an order it does not
        // hold: wait for the final's real answer (its sync may have returned the 2-second default first).
        // A final that went to the queue has no id — the closing then queues behind it (Order.sync).
        await Order.settled(final.hash);
        const advance = final.storedAdvance();
        if (!!Store.get('trainingMode') !== !!advance.isTraining())
            return;
        const before = advance.$toJson();
        const closing = AdvanceFlowService.closesInFull(advance, paid)
            ? await advance.prepareCancel()
            : await AdvanceFlowService.prepareClosing(advance, paid);
        // A Na rate advance's storno would take its Ostalo share off the buyer's debt; using the advance
        // pays nothing of that debt, so the closing leaves it.
        if (closing.getFlag(OrderFlagType.type).includes('installments'))
            closing.removeFlag(OrderFlagType.type);
        closing.addReference(final, 'advanceFinal');
        // The final's cashier issues its closing: by the final's answer they may have been logged out
        // ("Odjavi se" after the receipt), and a closing without a cashier is refused.
        closing.user_id = final.user_id;
        // The closing stamps the advance before it registers (onBeforeIssue). A registration that fails
        // gives the advance back as it was. Nobody waits on this closing, so a refusal is reported: EFI
        // then never hears that the advance was used. It only corrects the advance and is never handed
        // to the customer, so it is not printed (a full one, the advance's storno, would print).
        const result = await closing.issue({ skipPrintReceipt: true, skipPrintSlips: true });
        if (result.status === 'failed') {
            Order.insert({ data: [before] });
            Order.errorReporter.reportMessage(`Advance closing refused: ${result.error ?? ''}`, { advance: advance.hash, final: final.hash });
        }
    },
    /**
     * A closing that takes `amount` off `advance`: the advance's own lines at quantity −1, paid back the
     * way the advance was paid, the advance's buyer.
     */
    async prepareClosing(advance, amount) {
        const order = await Order.createNew();
        order.export = advance.export;
        order.client_id = advance.client_id || null;
        order.setFlag(OrderFlagType.type, 'closeAdvance', 'correct');
        order.items = AdvanceFlowService.closingLines(advance, amount, order);
        order.addReference(advance, 'corrective');
        const paymentKey = advance.getPayments().find(payment => payment.amount > 0 && payment.key !== PaymentTypeLabel.advance)?.key || PaymentTypeLabel.cash;
        order.close({
            status: OrderStatusType.advance,
            payments: [{ key: paymentKey, amount: order.getLeftToPay().toRoundedUnit(4) }]
        });
        return order;
    },
    /**
     * The lines of a closing that takes `amount` off `advance`, in line order: each a copy of its advance
     * line at quantity −1, priced with VAT, that names the line it reduces. Earlier closings that name a
     * line reduce that line; earlier closings that name none (a generic "Avans" line, the old app's
     * closings) come off the lines in line order.
     */
    closingLines(advance, amount, closing) {
        const closedByLine = new Map();
        for (const earlier of advance.correctedByOrders()) {
            for (const item of earlier.items) {
                if (item.ref_order_hash !== advance.hash || !item.ref_order_item_hash)
                    continue;
                closedByLine.set(item.ref_order_item_hash, (closedByLine.get(item.ref_order_item_hash) ?? ZERO()).add(item.getToPay()));
            }
        }
        const lines = advance.items.filter(item => item.getToPay().toUnit() > 0);
        const remaining = lines.map(item => positive(item.getToPay().add(closedByLine.get(item.hash) ?? ZERO())));
        let unnamed = remaining.reduce((acc, left) => acc.add(left), ZERO()).subtract(positive(advance.getRemainingAdvanceAmount()));
        for (let i = 0; i < remaining.length && unnamed.toUnit() > 0; i++) {
            const take = min(remaining[i], unnamed);
            remaining[i] = remaining[i].subtract(take);
            unnamed = unnamed.subtract(take);
        }
        let rest = amount;
        const takes = lines.map(() => ZERO());
        for (let i = 0; i < lines.length && rest.toUnit() > 0; i++) {
            takes[i] = min(remaining[i], rest);
            rest = rest.subtract(takes[i]);
        }
        // The lines cannot absorb the whole amount (inconsistent history): the last line takes the rest,
        // so the closing still reduces exactly the amount used.
        if (rest.toUnit() > 0 && lines.length)
            takes[takes.length - 1] = takes[takes.length - 1].add(rest);
        return lines.flatMap((line, i) => takes[i].toUnit() > 0 ? [mirror(line, takes[i], advance, closing)] : []);
    },
    /**
     * Route 1: when `storno` cancels a final that paid from an advance, undo the final's closing(s), so
     * the advance gets back what the final used. Returns the message to show when an undo is refused —
     * the closing then offers its own "Poništi" (Order.allowAdvanceClosingUndo).
     */
    async reopen(storno) {
        if (storno.getFlag(OrderFlagType.correctType)[0] !== 'cancel')
            return undefined;
        // The storno's copy of the final carries its payments but none of its references.
        const final = storno.correctsOrders()[0];
        if (!final || !final.isPaid() || final.getPaidAdvance().toUnit() <= 0)
            return undefined;
        const failed = I18n.t('order.advance_reopen_failed');
        let closings;
        try {
            closings = await AdvanceFlowService.closingsOf(final);
        }
        catch {
            return failed;
        }
        for (const closing of closings) {
            if ((await AdvanceFlowService.undo(closing)).status === 'failed')
                return failed;
        }
        return undefined;
    },
    /**
     * The live closings that name `final`: this register's own, else the server's (an older final, or one
     * closed on another register).
     */
    async closingsOf(final) {
        const stored = Order.all()
            .filter(order => !order.canceled() && order.refers_to.some(ref => ref.hash === final.hash && ref.reference?.type === 'advanceFinal'));
        if (stored.length || !final.id || !connectionService.isOnline)
            return stored;
        const [server] = await fetchOrders([final.id]);
        const ids = (server?.referred_by ?? [])
            .filter(order => order.reference?.type === 'advanceFinal' && !order.canceled_at && !!order.id)
            .map(order => order.id);
        if (!ids.length)
            return [];
        // A closing is stored in full: its undo needs its own references (the advance it corrects).
        const closings = await fetchOrders(ids);
        Order.insert({ data: closings });
        return closings.map(closing => Order.find(closing.hash));
    },
    /**
     * The undo of a closing: its storno, also carrying the closing's `type,closeAdvance…` flag, so client
     * transactions and the fiscal report treat it exactly as they treated the closing.
     */
    async undo(closing) {
        const type = closing.getFlag(OrderFlagType.type);
        const undo = await closing.prepareCancel(type[0] === 'closeAdvance' ? { flag: [OrderFlagType.type, ...type].join(',') } : {});
        // The undo stamps the closing before it registers (onBeforeIssue). A registration that fails gives
        // the closing back as it was, so its "Poništi" stays offered.
        const before = (Order.find(closing.hash) ?? closing).$toJson();
        const result = await undo.issue({ skipPrintReceipt: true, skipPrintSlips: true });
        if (result.status === 'failed') {
            Order.insert({ data: [before] });
            return result;
        }
        // What the server does with the undo, done on this register's advance too (the answer may still be
        // on its way, or queued): the closing no longer reduces the advance, which is open again. The
        // advance keeps its other finals and closings — a later final from it is offered what is left.
        // The cancelled final the closing names goes too, as its storno does on the server: until the
        // answer arrives (offline, or still on its way) the advance would still count that final's use.
        const advanceHash = closing.refers_to.find(ref => ref.reference?.type === 'corrective')?.hash;
        const advance = advanceHash ? Order.find(advanceHash) : undefined;
        if (advance) {
            const finals = closing.refers_to.filter(ref => ref.reference?.type === 'advanceFinal').map(ref => ref.hash);
            advance.canceled_at = null;
            advance.canceled_by_user_id = null;
            advance.removeFlag(OrderFlagType.corrected);
            advance.referred_by
                .filter(order => order.hash === closing.hash || finals.includes(order.hash))
                .forEach(order => { order.canceled_at = undo.last_activity_at; });
            Order.insert({ data: [advance.$toJson()] });
        }
        return result;
    },
};
/** Orders by id as the server holds them, with their items and references. */
async function fetchOrders(ids) {
    const response = await apiService.get('/orders/issue-result', { params: { order_ids: ids } });
    return response.data.orders;
}
/**
 * A closing line for `amount` of `line`. Its price always includes VAT — even for a line priced before
 * tax — so the closing takes exactly the amount used.
 */
function mirror(line, amount, advance, closing) {
    return new OrderItem({
        hash: HashGen.long(),
        order_id: closing.id,
        order_hash: closing.hash,
        product_id: line.product_id,
        name: line.name,
        gtin: line.gtin,
        unit_of_measure: line.unit_of_measure,
        tax_rate_id: line.tax_rate_id,
        tax_rate: line.tax_rate,
        exempt_from_tax: line.exempt_from_tax,
        reverse_calculation: false,
        unit_price: amount.toFormat('0.0000'),
        count: '-1.000',
        discount_percent: '0.00',
        ref_order_hash: advance.hash,
        ref_order_item_hash: line.hash,
    });
}
export default AdvanceFlowService;
export { AdvanceFlowService };
//# sourceMappingURL=AdvanceFlowService.js.map